import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {fetchUploadedInvoiceDetail} from '../../actions/invoiceUpload.action';
import {
  DetailWrapper,
  BackLink,
  PageTitle,
  PageIntro,
  SectionTitle,
  OverviewCard,
  OverviewItem,
  SavingsSummary,
  SavingsValue,
  LineItemCard,
  LineItemHeader,
  LineItemDescription,
  LineItemDetails,
  LineItemMatch,
  MatchInfo,
  ProductLink,
  SavingBadge,
  NoMatchLabel,
  StatusBadge,
  LoadingWrapper,
  ErrorMessage,
  ProcessingMessage,
} from './InvoiceUploadPage.style';
import Loading from '../../components/Loading/Loading';
import {GoogleAnalyticsHelper} from '../../helpers/googleAnalytics.helper';
import {
  ANALYTICS_EVENTS,
  INVOICE_UPLOAD_STATUS_LABELS,
} from '../../constants/constants';
import {formatDate, formatCurrency} from '../../helpers/invoiceUpload.helper';

const InvoiceUploadDetail = ({match, history}) => {
  const dispatch = useDispatch();
  const id = match.params.id;
  const uploadState = useSelector(state => state.invoiceUpload || {});
  const auth = useSelector(state => state.auth);
  const detail = uploadState.detail || null;
  const detailLoading = uploadState.detailLoading || false;
  const detailError = uploadState.detailError || null;

  useEffect(() => {
    if (auth && id) {
      dispatch(fetchUploadedInvoiceDetail(id));
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.COMPARE_PRICES_VIEW_DETAIL,
        {category: 'Compare Prices', invoiceId: id}
      );
    }
  }, [auth, id]);

  const goBack = () => history.push('/invoice-upload');

  if (detailLoading) {
    return (
      <DetailWrapper>
        <LoadingWrapper>
          <Loading isLight={false} />
        </LoadingWrapper>
      </DetailWrapper>
    );
  }

  if (detailError) {
    return (
      <DetailWrapper>
        <BackLink onClick={goBack}>&lsaquo; Back to Compare Prices</BackLink>
        <ErrorMessage>{detailError}</ErrorMessage>
      </DetailWrapper>
    );
  }

  if (!detail) {
    return (
      <DetailWrapper>
        <BackLink onClick={goBack}>&lsaquo; Back to Compare Prices</BackLink>
        <ErrorMessage>Invoice not found.</ErrorMessage>
      </DetailWrapper>
    );
  }

  const {overview, savingsData} = detail;
  const isProcessing =
    overview.status === 'PROCESSING' ||
    overview.status === 'READY_FOR_REVIEW' ||
    overview.status === 'SUBMITTED';
  const isFailed = overview.status === 'FAILED';
  const isComplete =
    overview.status === 'MATCHED' || overview.status === 'EMAIL_SENT';

  return (
    <DetailWrapper>
      <BackLink onClick={goBack}>&lsaquo; Back to Compare Prices</BackLink>
      <PageTitle>
        Compare Prices
        {overview.supplierName && ` \u2014 ${overview.supplierName}`}
      </PageTitle>

      <OverviewCard>
        <OverviewItem>
          <label>Status</label>
          <StatusBadge $status={overview.status}>
            {INVOICE_UPLOAD_STATUS_LABELS[overview.status] || overview.status}
          </StatusBadge>
        </OverviewItem>
        <OverviewItem>
          <label>Supplier</label>
          <span>{overview.supplierName || '-'}</span>
        </OverviewItem>
        <OverviewItem>
          <label>Invoice No.</label>
          <span>{overview.invoiceNumber || '-'}</span>
        </OverviewItem>
        <OverviewItem>
          <label>Invoice Date</label>
          <span>{formatDate(overview.invoiceDate)}</span>
        </OverviewItem>
        <OverviewItem>
          <label>Uploaded</label>
          <span>{formatDate(overview.uploadedAt)}</span>
        </OverviewItem>
        {overview.lineItemCount != null && (
          <OverviewItem>
            <label>Items</label>
            <span>
              {overview.matchedItemCount || 0} / {overview.lineItemCount}{' '}
              matched
            </span>
          </OverviewItem>
        )}
      </OverviewCard>

      {isProcessing && (
        <ProcessingMessage>
          <h3>We&apos;re analysing your invoice</h3>
          <p>
            This usually takes a few minutes. Check back shortly to see your
            savings breakdown.
          </p>
        </ProcessingMessage>
      )}

      {isFailed && (
        <ErrorMessage>
          We couldn&apos;t process this invoice. Please try uploading it again
          or contact customer support if the problem persists.
        </ErrorMessage>
      )}

      {isComplete && !savingsData && (
        <ProcessingMessage>
          <h3>Savings data unavailable</h3>
          <p>
            This invoice has been processed but the detailed savings breakdown
            is not available. Please contact customer support if you need
            assistance.
          </p>
        </ProcessingMessage>
      )}

      {savingsData && (
        <>
          <SavingsSummary>
            <SavingsValue>
              <label>Your invoice total</label>
              <span>{formatCurrency(savingsData.invoiceTotal)}</span>
            </SavingsValue>
            <SavingsValue>
              <label>JJ total</label>
              <span>{formatCurrency(savingsData.effectiveTotal)}</span>
            </SavingsValue>
            <SavingsValue $positive={savingsData.totalSavings > 0}>
              <label>Total savings</label>
              <span>
                {formatCurrency(savingsData.totalSavings)} (
                {(savingsData.savingsPercent || 0).toFixed(1)}%)
              </span>
            </SavingsValue>
            <SavingsValue>
              <label>Items matched</label>
              <span>
                {savingsData.matchedLineItems} of {savingsData.totalLineItems}
              </span>
            </SavingsValue>
          </SavingsSummary>

          <SectionTitle>Item-by-item comparison</SectionTitle>
          <PageIntro>
            <p>
              Click any JJ product below to view full details, check
              availability, and add it straight to your basket.
            </p>
          </PageIntro>

          {savingsData.lineItems.map((item, idx) => (
            <LineItemCard key={idx}>
              <LineItemHeader>
                <div>
                  <LineItemDescription>{item.description}</LineItemDescription>
                  <LineItemDetails>
                    Qty: {item.invoiceQuantity} &times;{' '}
                    {formatCurrency(item.invoiceUnitPrice)} ={' '}
                    {formatCurrency(item.invoiceLineTotal)}
                  </LineItemDetails>
                </div>
              </LineItemHeader>

              {item.noMatchAvailable ? (
                <NoMatchLabel>No JJ alternative available</NoMatchLabel>
              ) : (
                <LineItemMatch>
                  <MatchInfo>
                    <strong>
                      {item.matchedProductId ? (
                        <ProductLink
                          href={`/productDetail/${item.matchedProductId}`}
                          onClick={e => {
                            e.preventDefault();
                            GoogleAnalyticsHelper.trackEvent(
                              ANALYTICS_EVENTS.COMPARE_PRICES_PRODUCT_CLICK,
                              {
                                category: 'Compare Prices',
                                productId: item.matchedProductId,
                                productName: item.matchedProductName,
                                saving: item.saving,
                              }
                            );
                            history.push(
                              `/productDetail/${item.matchedProductId}`
                            );
                          }}
                        >
                          {item.matchedProductName} ({item.matchedProductId})
                        </ProductLink>
                      ) : (
                        item.matchedProductName
                      )}
                    </strong>
                    JJ price: {formatCurrency(item.matchedProductPrice)} |
                    Total: {formatCurrency(item.effectiveTotal)}
                  </MatchInfo>
                  {item.saving != null && (
                    <SavingBadge $positive={item.saving > 0}>
                      {item.saving > 0 ? 'Save ' : ''}
                      {formatCurrency(Math.abs(item.saving))}
                      {item.saving < 0 ? ' more' : ''}
                    </SavingBadge>
                  )}
                </LineItemMatch>
              )}
            </LineItemCard>
          ))}
        </>
      )}
    </DetailWrapper>
  );
};

InvoiceUploadDetail.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default InvoiceUploadDetail;
